import { motion } from "framer-motion";
import {
  MessageSquare,
  Star,
  ThumbsUp,
  Download,
  Mail,
  Phone,
  Instagram,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Filter,
} from "lucide-react";
import { useState, useMemo } from "react";
import { Link } from "wouter";
import {
  useAdminFeedback,
  useAdminFeedbackStats,
  type FeedbackFilters,
} from "../api/useAdminFeedback";
import { Loader } from '@/components/Loader';
import { EVENT_TYPE_CONFIG } from '@/utils/eventTypeUtils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const ITEMS_PER_PAGE = 25;

// Date range presets. `days` is null for "all time" and for a custom range.
type RangePreset = "30d" | "90d" | "180d" | "365d" | "all" | "custom";
const RANGE_PRESETS: { value: RangePreset; label: string; days: number | null }[] = [
  { value: "30d", label: "30 Days", days: 30 },
  { value: "90d", label: "90 Days", days: 90 },
  { value: "180d", label: "6 Months", days: 180 },
  { value: "365d", label: "1 Year", days: 365 },
  { value: "all", label: "All Time", days: null },
  { value: "custom", label: "Custom", days: null },
];
const DEFAULT_RANGE_PRESET: RangePreset = "90d";

const EVENT_TYPE_OPTIONS: { value: FeedbackFilters["eventType"]; label: string }[] = [
  { value: "all", label: "All Types" },
  ...Object.entries(EVENT_TYPE_CONFIG).map(([value, cfg]) => ({
    value: value as FeedbackFilters["eventType"],
    label: `${cfg.emoji} ${cfg.label}`,
  })),
];

const toDateInput = (d: Date) => d.toISOString().split("T")[0];

// Inclusive YYYY-MM-DD range ending today and starting `days - 1` days ago.
function presetRange(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { start: toDateInput(start), end: toDateInput(end) };
}

// Types for feedback items
type FeedbackType = "post-dinner" | "review" | "general";

interface UnifiedFeedbackItem {
  id: string;
  type: FeedbackType;
  createdAt: string;
  data: any;
}

// User Contact Info Component
function UserContactInfo({ user }: { user: any }) {
  if (!user) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-dark-muted">
      {user.email && (
        <a
          href={`mailto:${user.email}`}
          className="flex items-center gap-1 hover:text-primary-500 transition-colors min-w-0"
        >
          <Mail className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate max-w-[200px]">{user.email}</span>
        </a>
      )}
      {user.phone && (
        <a
          href={`tel:${user.phone}`}
          className="flex items-center gap-1 hover:text-primary-500 transition-colors whitespace-nowrap"
        >
          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{user.phone}</span>
        </a>
      )}
      {user.instagram && (
        <a
          href={`https://instagram.com/${user.instagram.replace("@", "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 hover:text-primary-500 transition-colors whitespace-nowrap"
        >
          <Instagram className="w-3.5 h-3.5 flex-shrink-0" />
          <span>@{user.instagram.replace("@", "")}</span>
        </a>
      )}
    </div>
  );
}

// Post-Dinner Feedback Card Component
function PostDinnerFeedbackCard({
  feedback,
  index,
}: {
  feedback: any;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className="card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 text-xs font-medium bg-primary-500/20 text-primary-400 rounded">
          Post-Dinner Survey
        </span>
      </div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
            <Link
              href={`/users?q=${feedback.userId}`}
              className="font-semibold hover:text-primary-500 transition-colors truncate"
              title={`View user: ${feedback.userId}`}
            >
              {feedback.user?.firstName} {feedback.user?.lastName}
            </Link>
            <span className="text-dark-muted text-sm whitespace-nowrap">
              {new Date(feedback.createdAt).toLocaleDateString()}
            </span>
          </div>
          {feedback.user && (
            <div className="mb-2 overflow-hidden">
              <UserContactInfo user={feedback.user} />
            </div>
          )}
          {feedback.booking?.eventTime && (
            <p className="text-dark-muted text-sm mb-2 truncate">
              Event:{" "}
              {new Date(feedback.booking.eventTime.date).toLocaleDateString()}
              {feedback.booking.restaurantName && (
                <> • {feedback.booking.restaurantName}</>
              )}
              {feedback.booking.neighborhoodName && (
                <> ({feedback.booking.neighborhoodName})</>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 space-x-4">
          {feedback.overallExperience && (
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-500">
                {feedback.overallExperience}/5
              </div>
              <div className="text-xs text-dark-muted whitespace-nowrap">Experience</div>
            </div>
          )}
          {feedback.recommendationScore && (
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-500">
                {feedback.recommendationScore}/10
              </div>
              <div className="text-xs text-dark-muted whitespace-nowrap">NPS</div>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <span className="text-dark-muted text-sm">Felt Welcomed: </span>
          <span
            className={
              feedback.feltWelcomed === "yes" ? "text-green-500" : "text-red-500"
            }
          >
            {feedback.feltWelcomed || "N/A"}
          </span>
        </div>
        <div>
          <span className="text-dark-muted text-sm">Future Attendance: </span>
          <span
            className={
              feedback.futureAttendance === "yes"
                ? "text-green-500"
                : "text-red-500"
            }
          >
            {feedback.futureAttendance || "N/A"}
          </span>
        </div>
        <div>
          <span className="text-dark-muted text-sm">Location/Timing: </span>
          <span>{feedback.locationTiming || "N/A"}</span>
        </div>
        <div>
          <span className="text-dark-muted text-sm">Restaurant: </span>
          <span>{feedback.restaurantSelection || "N/A"}</span>
        </div>
      </div>

      {feedback.notes && (
        <div className="mt-4 p-4 bg-dark-border/30 rounded-lg">
          <p className="text-sm font-medium mb-1">Additional Notes:</p>
          <p className="text-sm text-dark-muted">{feedback.notes}</p>
        </div>
      )}
    </motion.div>
  );
}

// Review Card Component
function ReviewCard({ review, index }: { review: any; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className="card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 text-xs font-medium bg-accent-500/20 text-accent-400 rounded">
          Review
        </span>
      </div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <Link
              href={`/users?q=${review.userId}`}
              className="font-semibold hover:text-primary-500 transition-colors"
              title={`View user: ${review.userId}`}
            >
              {review.user?.firstName} {review.user?.lastName}
            </Link>
            {review.rating && (
              <div className="flex items-center">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-yellow-500 text-yellow-500"
                  />
                ))}
              </div>
            )}
          </div>
          <p className="text-dark-muted text-sm mb-1">
            {new Date(review.createdAt).toLocaleDateString()}
          </p>
          {review.user && <UserContactInfo user={review.user} />}
        </div>
      </div>
      {review.comment && <p className="text-sm">{review.comment}</p>}
    </motion.div>
  );
}

// General Feedback Card Component
function GeneralFeedbackCard({
  feedback,
  index,
}: {
  feedback: any;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className="card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded">
          General Feedback
        </span>
      </div>
      <div className="flex items-center space-x-2 mb-2">
        <Link
          href={`/users?q=${feedback.userId}`}
          className="font-semibold hover:text-primary-500 transition-colors"
          title={`View user: ${feedback.userId}`}
        >
          {feedback.user?.firstName} {feedback.user?.lastName}
        </Link>
        <span className="text-dark-muted text-sm">
          {new Date(feedback.createdAt).toLocaleDateString()}
        </span>
      </div>
      {feedback.user && (
        <div className="mb-2">
          <UserContactInfo user={feedback.user} />
        </div>
      )}
      <p className="text-sm">{feedback.feedback}</p>
    </motion.div>
  );
}

// Unified Feedback Card - renders the appropriate card type
function UnifiedFeedbackCard({
  item,
  index,
}: {
  item: UnifiedFeedbackItem;
  index: number;
}) {
  switch (item.type) {
    case "post-dinner":
      return <PostDinnerFeedbackCard feedback={item.data} index={index} />;
    case "review":
      return <ReviewCard review={item.data} index={index} />;
    case "general":
      return <GeneralFeedbackCard feedback={item.data} index={index} />;
  }
}

// Pagination Component
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    const end = Math.min(totalPages, start + showPages - 1);

    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {getPageNumbers().map((page, i) =>
        typeof page === "string" ? (
          <span key={`ellipsis-${i}`} className="px-2 text-dark-muted">
            {page}
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentPage === page
                ? "bg-primary-500 text-white"
                : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function AdminFeedback() {
  const [feedbackType, setFeedbackType] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Event type + date range filters (apply to stats, chart and list alike)
  const [eventType, setEventType] = useState<FeedbackFilters["eventType"]>("all");
  const [rangePreset, setRangePreset] = useState<RangePreset>(DEFAULT_RANGE_PRESET);
  const [customStartDate, setCustomStartDate] = useState<string>(presetRange(90).start);
  const [customEndDate, setCustomEndDate] = useState<string>(presetRange(90).end);

  const filters = useMemo<FeedbackFilters>(() => {
    const preset = RANGE_PRESETS.find((p) => p.value === rangePreset);
    if (rangePreset === "custom") {
      return { eventType, startDate: customStartDate || undefined, endDate: customEndDate || undefined };
    }
    if (preset?.days) {
      const { start, end } = presetRange(preset.days);
      return { eventType, startDate: start, endDate: end };
    }
    return { eventType };
  }, [eventType, rangePreset, customStartDate, customEndDate]);

  const { data: feedbackData, isLoading } = useAdminFeedback(filters);
  const { data: statsData, isLoading: statsLoading } = useAdminFeedbackStats(filters);

  const handleEventTypeChange = (type: FeedbackFilters["eventType"]) => {
    setEventType(type);
    setCurrentPage(1);
  };
  const handleRangePresetChange = (preset: RangePreset) => {
    setRangePreset(preset);
    setCurrentPage(1);
  };

  // Transform stats for chart
  const chartData = useMemo(() => {
    if (!statsData?.data) return [];
    return statsData.data.map((stat) => ({
      date: stat.eventDate,
      nps: stat.avgNps,
      experience: stat.avgExperience,
      futureAttendance: stat.futureAttendancePct,
      futureAttendanceResponses: stat.futureAttendanceResponses,
      responses: stat.responseCount,
    }));
  }, [statsData]);

  const allFeedback = feedbackData?.data || {
    postDinner: [],
    general: [],
    reviews: [],
  };
  const { postDinner = [], general = [], reviews = [] } = allFeedback;

  // Create unified list for "all" view - interspersed by date
  const unifiedFeedback = useMemo(() => {
    const items: UnifiedFeedbackItem[] = [
      ...postDinner.map((f: any) => ({
        id: f.id,
        type: "post-dinner" as const,
        createdAt: f.createdAt,
        data: f,
      })),
      ...reviews.map((r: any) => ({
        id: r.id,
        type: "review" as const,
        createdAt: r.createdAt,
        data: r,
      })),
      ...general.map((g: any) => ({
        id: g.id,
        type: "general" as const,
        createdAt: g.createdAt,
        data: g,
      })),
    ];

    // Sort by date, newest first
    return items.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [postDinner, reviews, general]);

  // Get the items for current filter
  const filteredItems = useMemo(() => {
    switch (feedbackType) {
      case "post-dinner":
        return postDinner
          .slice()
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .map((f: any) => ({
            id: f.id,
            type: "post-dinner" as const,
            createdAt: f.createdAt,
            data: f,
          }));
      case "reviews":
        return reviews
          .slice()
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .map((r: any) => ({
            id: r.id,
            type: "review" as const,
            createdAt: r.createdAt,
            data: r,
          }));
      case "general":
        return general
          .slice()
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .map((g: any) => ({
            id: g.id,
            type: "general" as const,
            createdAt: g.createdAt,
            data: g,
          }));
      default:
        return unifiedFeedback;
    }
  }, [feedbackType, postDinner, reviews, general, unifiedFeedback]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filter changes
  const handleFilterChange = (type: string) => {
    setFeedbackType(type);
    setCurrentPage(1);
  };

  // Calculate summary metrics
  const totalFeedback = postDinner.length + general.length + reviews.length;
  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum: number, r: any) => sum + (r.rating || 0), 0) /
          reviews.filter((r: any) => r.rating).length
        ).toFixed(1)
      : "N/A";
  const averageExperience =
    postDinner.length > 0
      ? (
          postDinner.reduce(
            (sum: number, f: any) => sum + (f.overallExperience || 0),
            0
          ) / postDinner.filter((f: any) => f.overallExperience).length
        ).toFixed(1)
      : "N/A";
  const npsScore =
    postDinner.length > 0
      ? (
          postDinner.reduce(
            (sum: number, f: any) => sum + (f.recommendationScore || 0),
            0
          ) / postDinner.filter((f: any) => f.recommendationScore).length
        ).toFixed(1)
      : "N/A";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-2">Feedback</h1>
        <p className="text-dark-muted text-lg">
          User feedback, reviews, and post-dinner surveys
        </p>
      </motion.div>

      {/* Event Type & Date Range Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="card mb-8"
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-primary-500" />
          <h3 className="text-sm font-semibold">Filters</h3>
        </div>
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-dark-muted">Event Type</label>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleEventTypeChange(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    eventType === opt.value
                      ? "bg-primary-500 text-white"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {eventType !== "all" && (
              <p className="text-xs text-dark-muted">
                General feedback isn't tied to an event, so it's hidden while an event type is selected.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-dark-muted">Date Range</label>
            <div className="flex flex-wrap gap-2">
              {RANGE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handleRangePresetChange(preset.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    rangePreset === preset.value
                      ? "bg-primary-500 text-white"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {rangePreset === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  aria-label="Start date"
                  value={customStartDate}
                  onChange={(e) => {
                    setCustomStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  max={customEndDate || undefined}
                  className="bg-dark-border text-white px-3 py-1.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none text-sm"
                />
                <span className="text-dark-muted">to</span>
                <input
                  type="date"
                  aria-label="End date"
                  value={customEndDate}
                  onChange={(e) => {
                    setCustomEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  min={customStartDate || undefined}
                  className="bg-dark-border text-white px-3 py-1.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none text-sm"
                />
              </div>
            )}
            <p className="text-xs text-dark-muted">
              {filters.startDate && filters.endDate
                ? `${new Date(`${filters.startDate}T00:00:00`).toLocaleDateString()} – ${new Date(`${filters.endDate}T00:00:00`).toLocaleDateString()}`
                : "All dates"}
              {" · "}list by submission date, chart by event date
            </p>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalFeedback}</p>
              <p className="text-dark-muted text-sm">Total Feedback</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-accent-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{averageRating}</p>
              <p className="text-dark-muted text-sm">Avg Rating</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <ThumbsUp className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{averageExperience}</p>
              <p className="text-dark-muted text-sm">Avg Experience (1-5)</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <ThumbsUp className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{npsScore}</p>
              <p className="text-dark-muted text-sm">NPS Score (1-10)</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* NPS, Experience & Future Attendance Chart */}
      {(statsLoading || chartData.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary-500" />
            <h3 className="text-lg font-semibold">NPS, Experience & Future Attendance by Event Date</h3>
          </div>
          <div className="h-72">
            {statsLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="date"
                    stroke="#888"
                    tick={{ fill: "#888", fontSize: 12 }}
                    tickFormatter={(value) => {
                      const d = new Date(value);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={[0, 10]}
                    stroke="#888"
                    tick={{ fill: "#888", fontSize: 12 }}
                    label={{
                      value: "NPS (0-10)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#888",
                      fontSize: 12,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 5]}
                    stroke="#888"
                    tick={{ fill: "#888", fontSize: 12 }}
                    label={{
                      value: "Experience (0-5)",
                      angle: 90,
                      position: "insideRight",
                      fill: "#888",
                      fontSize: 12,
                    }}
                  />
                  {/* Percentage axis for future attendance; hidden so the
                      chart keeps its two labelled axes, values are in the
                      tooltip. */}
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} hide />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1a1a2e",
                      border: "1px solid #333",
                      borderRadius: "8px",
                    }}
                    labelFormatter={(value) =>
                      new Date(value).toLocaleDateString()
                    }
                    formatter={(value: number, name: string, item: any) => {
                      const responses = item?.payload?.responses;
                      const suffix = responses
                        ? ` (n=${responses})`
                        : "";
                      if (name === "nps")
                        return [`${value?.toFixed(1)}${suffix}`, "Avg NPS"];
                      if (name === "experience")
                        return [
                          `${value?.toFixed(1)}${suffix}`,
                          "Avg Experience",
                        ];
                      if (name === "futureAttendance") {
                        const answered = item?.payload?.futureAttendanceResponses;
                        return [
                          `${value?.toFixed(0)}%${answered ? ` (n=${answered})` : ""}`,
                          "Would attend again",
                        ];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend
                    formatter={(value) => {
                      if (value === "nps") return "Avg NPS (0-10)";
                      if (value === "experience") return "Avg Experience (1-5)";
                      if (value === "futureAttendance") return "Would attend again (%)";
                      return value;
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="nps"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ fill: "#f97316", r: 4 }}
                    connectNulls
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="experience"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: "#22c55e", r: 4 }}
                    connectNulls
                  />
                  <Line
                    yAxisId="pct"
                    type="monotone"
                    dataKey="futureAttendance"
                    stroke="#38bdf8"
                    strokeWidth={2}
                    strokeDasharray="5 3"
                    dot={{ fill: "#38bdf8", r: 4 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <p className="text-xs text-dark-muted mt-2 text-center">
            Based on {chartData.reduce((sum, d) => sum + d.responses, 0)} survey
            responses across {chartData.length} event dates
          </p>
        </motion.div>
      )}

      {/* Feedback Type Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card mb-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-sm font-medium">View:</label>
            <div className="flex gap-2">
              {[
                { key: "all", label: "All" },
                { key: "post-dinner", label: "Post-Dinner" },
                { key: "reviews", label: "Reviews" },
                { key: "general", label: "General" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleFilterChange(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    feedbackType === key
                      ? "bg-primary-500 text-white"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-dark-muted">
              {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </span>
            {feedbackType === "post-dinner" && postDinner.length > 0 && (
              <button className="btn btn-ghost">
                <Download className="w-4 h-4 mr-2 inline-block" />
                Export
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Feedback List */}
      {paginatedItems.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="space-y-4">
            {paginatedItems.map((item, index) => (
              <UnifiedFeedbackCard key={item.id} item={item} index={index} />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </motion.div>
      ) : (
        <div className="card text-center py-16">
          <MessageSquare className="w-16 h-16 text-dark-muted mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No feedback for these filters</h3>
          <p className="text-dark-muted">
            Try a wider date range or a different event type. Feedback appears here as users submit surveys and reviews.
          </p>
        </div>
      )}
    </div>
  );
}
